import React from 'react';
import { ModDefinition, getActiveMods, getAllMods } from '../utils/ModRegistry';
import { XIcon, PlusIcon } from './Icons';
import { useAuth } from '../contexts/AuthContext';
import { stickerTextStyle } from './StickerText';

interface ModLibraryModalProps {
    isOpen: boolean;
    onClose: () => void;
    activeMods: string[];
    onSelectMod: (modId: string) => void;
    onOpenStore: () => void;
    usersData: any;
    dragProgress?: number;
}

export const ModLibraryModal = ({
    isOpen,
    onClose,
    activeMods,
    onSelectMod,
    onOpenStore,
    usersData = {},
    dragProgress
}: ModLibraryModalProps) => {
    const { currentUser, usersData: authUsersData } = useAuth();
    const isVisible = isOpen || (dragProgress !== undefined && dragProgress > 0);
    if (!isVisible) return null;

    const activeUserId = currentUser;
    const activeUsersData = { ...usersData, ...authUsersData };
    const currentUserData = activeUserId ? activeUsersData[activeUserId] : null;
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    // getAllMods is now exported from ModRegistry.ts
    const allMods = getAllMods(usersData, activeUserId);
    const installedMods = getActiveMods(activeMods, usersData, activeUserId);

    return (
        <div 
            className={`fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 ${isOpen ? 'animate-fade-in' : ''}`} 
            style={{ 
                backdropFilter: 'blur(8px)', 
                WebkitBackdropFilter: 'blur(8px)',
                opacity: isOpen ? 1 : dragProgress,
                pointerEvents: isOpen ? 'auto' : 'none'
            }} 
            onClick={onClose}
        >
            <div 
                style={{ 
                    backgroundColor: modalBg, 
                    color: userTextColor,
                    transform: isOpen ? 'none' : `scale(${0.95 + (dragProgress || 0) * 0.05})`
                }} 
                onClick={e => e.stopPropagation()} 
                className={`relative w-full max-w-md rounded-[2.5rem] border border-white/10 shadow-2xl overflow-hidden ${isOpen ? 'animate-scale-up' : ''}`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/5">
                    <div>
                        <h2 className="text-xl font-black tracking-tight" style={{ color: userTextColor, ...stickerTextStyle(0.15) }}>Mod Library</h2>
                        <p className="text-xs font-medium opacity-60" style={stickerTextStyle(0.18)}>Switch your active modular slot</p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-white/10 transition-colors"
                    >
                        <XIcon className="w-6 h-6" style={{ color: userTextColor }} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
                    <div className="grid grid-cols-1 gap-4">
                        {installedMods.map(mod => (
                            <button
                                key={mod.id}
                                onClick={() => {
                                    onSelectMod(mod.id);
                                    onClose();
                                }}
                                className="group flex items-center gap-4 p-4 rounded-3xl bg-black/20 hover:bg-black/40 border border-white/5 transition-all active:scale-[0.98]"
                            >
                                <div className="relative w-12 h-12 rounded-full overflow-hidden shadow-lg group-hover:scale-105 transition-transform">
                                    {typeof mod.icon === 'string' ? (
                                        <img src={mod.icon} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: userThemeColor }}>
                                            <mod.icon className="w-6 h-6 text-white" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 text-left">
                                    <h3 className="font-bold text-sm" style={stickerTextStyle(0.18)}>{mod.name}</h3>
                                    <p className="text-[10px] opacity-60 line-clamp-1" style={stickerTextStyle(0.18)}>{mod.description}</p>
                                </div>
                                <div className="w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: `${userThemeColor}33` }}>
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: userThemeColor }} />
                                </div>
                            </button>
                        ))}

                        <button
                            onClick={() => {
                                onOpenStore();
                                onClose();
                            }}
                            style={{
                                borderColor: `${userThemeColor}33`,
                                color: userTextColor,
                                backgroundColor: 'rgba(0, 0, 0, 0.1)'
                            }}
                            className="flex items-center justify-center gap-2 p-4 rounded-3xl border-2 border-dashed hover:bg-white/5 transition-all group"
                        >
                            <PlusIcon className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                            <span className="font-bold text-sm uppercase tracking-wider" style={stickerTextStyle(0.18)}>PIMP YOUR RIDE</span>
                        </button>
                    </div>
                </div>

                {/* Footer Tip */}
                <div className="p-4 bg-white/5 text-center">
                    <p className="text-[9px] text-gray-500 uppercase tracking-widest font-black" style={stickerTextStyle(0.18)}>
                        Tip: Long-press the icon in the NavBar to switch
                    </p>
                </div>
            </div>
        </div>
    );
};
